import type { CVListItemDto } from '@cibli/shared';

import { differenceInCalendarDays } from '@cibli/shared';
import { Check, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ICON_PALETTES = [
  { bg: 'bg-gradient-to-br from-cj-green-light to-cj-blue', icon: 'text-cj-green-dark' },
  { bg: 'bg-gradient-to-br from-cj-yellow to-cj-orange/20', icon: 'text-cj-orange-nuance' },
  { bg: 'bg-gradient-to-br from-cj-pink/25 to-cj-aubergine/15', icon: 'text-cj-aubergine' },
];

interface CVMiniatureSelectableProps {
  cv: CVListItemDto;
  isSelected: boolean;
  onSelect: (cvId: string) => void;
}

function formatRelativeDate(dateString: string, t: ReturnType<typeof useTranslation>['t']): string {
  const date = new Date(dateString);
  const diffDays = differenceInCalendarDays(new Date(), date);

  if (diffDays === 0) return t('dashboard.cvList.card.today');
  if (diffDays === 1) return t('dashboard.cvList.card.yesterday');
  if (diffDays < 7) return t('dashboard.cvList.card.daysAgo', { days: diffDays });
  if (diffDays < 14) return t('dashboard.cvList.card.oneWeekAgo');
  if (diffDays < 30)
    return t('dashboard.cvList.card.weeksAgo', { weeks: Math.floor(diffDays / 7) });
  return t('dashboard.cvList.card.monthsAgo', { months: Math.floor(diffDays / 30) });
}

export default function CVMiniatureSelectable({
  cv,
  isSelected,
  onSelect,
}: CVMiniatureSelectableProps) {
  const { t } = useTranslation();
  const palette = ICON_PALETTES[cv.id.charCodeAt(0) % ICON_PALETTES.length];

  return (
    <button
      type="button"
      onClick={() => onSelect(cv.id)}
      aria-pressed={isSelected}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cj-green focus-visible:ring-offset-1 ${
        isSelected
          ? 'border-cj-green bg-cj-green/8 shadow-sm'
          : 'border-cj-green-dark/8 bg-white hover:border-cj-green-dark/20 hover:bg-cj-green-light/10'
      }`}
    >
      <div
        className={`w-10 h-12 ${palette.bg} rounded-lg flex items-center justify-center flex-shrink-0`}
      >
        {isSelected ? (
          <Check className="w-5 h-5 text-cj-green stroke-[3]" />
        ) : (
          <FileText className={`w-5 h-5 ${palette.icon}`} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm text-cj-green-dark truncate">{cv.jobTitle}</h4>
        <p className="text-[11px] text-cj-green-dark/40 mt-0.5">
          {formatRelativeDate(cv.updatedAt, t)}
        </p>
      </div>

      {isSelected && (
        <span className="flex-shrink-0 w-6 h-6 bg-cj-green rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white stroke-[3]" />
        </span>
      )}
    </button>
  );
}
